import { useCallback, useId, useMemo } from 'react';
import { Button, InlineNotification } from '@carbon/react';
import { Add } from '@carbon/react/icons';
import { useAtomValue } from 'jotai';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { enumsLoadableAtom } from './atoms';
import { useScenarioForm } from './contexts';
import {
  availableOptions,
  categoryLabels,
  expandLines,
  hasFreeCombination,
  missingRateWarnings,
  overlapsByLine,
  setLineHurdle,
  setLineReserveCategories,
  type CategoryLabels,
  type CombinationUniverse,
} from './discount-rate-line-utils';
import { DiscountRateLine, type LineEnums } from './discount-rate-line';
import { hasFillablePrePermit, loadDefaultLines, nextId } from './utils';
import type {
  DiscountRateLine as Line,
  EnumOption,
  InterestType,
  RateCategory,
  ReserveCategory,
} from './types';
import styles from './discount-rate-table.module.scss';

interface DiscountRateTableProps {
  areaIndex: number;
}

const NO_OVERLAPS: string[] = [];

/**
 * @param {EnumOption[]} options - One picker's options
 * @param {string[]} offered - The values it offers, a line's own selections among them
 * @return {EnumOption[]} The options to list: those offered, and the disabled ones, which are
 *   never a choice but stay listed
 */
function keepOffered(options: EnumOption[], offered: string[]): EnumOption[] {
  return options.filter((o) => o.disabled || offered.includes(o.value));
}

interface DiscountRateLinesProps {
  areaIndex: number;
  lines: Line[];
  enums: LineEnums | null;
  labels: CategoryLabels;
  onLineChange: (lineIndex: number, patch: Partial<Line>) => void;
  onReserveCategoriesChange: (
    lineIndex: number,
    value: ReserveCategory[]
  ) => void;
  onHurdleChange: (lineIndex: number, value: number | null) => void;
  onRemove: (lineIndex: number) => void;
  onAdd: () => void;
  onLoadDefaults: (interestType: InterestType) => void;
}

/**
 * An area's lines, the missing-rates warning and the add / defaults buttons. Its own component
 * so the values derived from the lines are computed once per change, and each memoized line
 * re-renders only when its own props change.
 * @param {DiscountRateLinesProps} props - The component props
 * @return {JSX.Element} The lines and their controls
 */
function DiscountRateLines({
  areaIndex,
  lines,
  enums,
  labels,
  onLineChange,
  onReserveCategoriesChange,
  onHurdleChange,
  onRemove,
  onAdd,
  onLoadDefaults,
}: DiscountRateLinesProps): JSX.Element {
  const entries = useMemo(() => expandLines(lines), [lines]);
  const overlaps = useMemo(
    () => overlapsByLine(lines, labels),
    [lines, labels]
  );
  const universe = useMemo<CombinationUniverse | null>(
    () =>
      enums && {
        rateCategories: enums.rateCategories.map(
          (o) => o.value as RateCategory
        ),
        reserveCategories: enums.reserveCategories
          .filter((o) => !o.disabled)
          .map((o) => o.value as ReserveCategory),
        interestTypes: enums.interestTypes.map((o) => o.value as InterestType),
      },
    [enums]
  );
  // Each line's own option lists: only the values that complete a free combination, plus its
  // own selections and the disabled options, which are never a choice but stay listed.
  const lineEnums = useMemo(
    () =>
      enums &&
      universe &&
      new Map<number, LineEnums>(
        lines.map((line, i) => {
          const offered = availableOptions(lines, i, universe);
          return [
            line.id,
            {
              rateCategories: keepOffered(
                enums.rateCategories,
                offered.rateCategories
              ),
              reserveCategories: keepOffered(
                enums.reserveCategories,
                offered.reserveCategories
              ),
              interestTypes: keepOffered(
                enums.interestTypes,
                offered.interestTypes
              ),
            },
          ];
        })
      ),
    [lines, enums, universe]
  );
  const addNoteId = useId();
  const canAdd = useMemo(
    () => !universe || hasFreeCombination(lines, universe),
    [lines, universe]
  );
  const warnings = useMemo(
    () => missingRateWarnings(entries, labels),
    [entries, labels]
  );

  const hasWi = entries.some((e) => e.interestType === 'working_interest');
  const hasRi = entries.some((e) => e.interestType === 'royalty_interest');
  // Each Defaults button loads its lines once, then comes back while a blank pre-permit
  // line can be seeded at the LTD / Permit midpoint (loadDefaultLines).
  const wiDefaultsDone =
    hasWi && !hasFillablePrePermit(entries, 'working_interest');
  const riDefaultsDone =
    hasRi && !hasFillablePrePermit(entries, 'royalty_interest');

  return (
    <>
      {lines.length > 0 && (
        <div className={styles.rows}>
          {lines.map((line, lineIndex) => (
            <DiscountRateLine
              key={line.id}
              areaIndex={areaIndex}
              lineIndex={lineIndex}
              line={line}
              showLabels={lineIndex === 0}
              enums={lineEnums?.get(line.id) ?? null}
              overlaps={overlaps.get(line.id) ?? NO_OVERLAPS}
              onChange={onLineChange}
              onReserveCategoriesChange={onReserveCategoriesChange}
              onHurdleChange={onHurdleChange}
              onRemove={onRemove}
            />
          ))}
        </div>
      )}

      {lines.length === 0 && (
        <p className={styles.empty}>
          No rate lines yet. Add one below or load defaults.
        </p>
      )}

      {warnings.length > 0 && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          className={styles.warning}
          title="Missing rates"
        >
          <ul className={styles.warningList}>
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </InlineNotification>
      )}

      <div className={styles.footer}>
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Add}
          disabled={!canAdd}
          aria-describedby={canAdd ? undefined : addNoteId}
          onClick={onAdd}
        >
          Add Rate Line
        </Button>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={Add}
          disabled={wiDefaultsDone}
          onClick={() => onLoadDefaults('working_interest')}
        >
          Working Interest Defaults
        </Button>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={Add}
          disabled={riDefaultsDone}
          onClick={() => onLoadDefaults('royalty_interest')}
        >
          Royalty Interest Defaults
        </Button>
        {!canAdd && (
          <span id={addNoteId} className={styles.footerNote}>
            Every combination is already set. Remove a value from a line, or
            give a line an operator, to add another.
          </span>
        )}
      </div>
    </>
  );
}

/**
 * Editable discount rate lines for an area config. Each line applies one rate to every
 * combination of its rate, reserve and interest selections (one saved entry per combination).
 * Includes "Add Rate Line", "Working Interest Defaults" and "Royalty Interest Defaults" buttons,
 * and warns about any reserve category / interest type left without a target, max or mtm rate.
 * @param {DiscountRateTableProps} props - The component props
 * @return {JSX.Element} The discount rate lines accordion
 */
export function DiscountRateTable({
  areaIndex,
}: DiscountRateTableProps): JSX.Element {
  const { form } = useScenarioForm();
  const prefix = `areas[${areaIndex}].discountRateLines` as const;
  const enumsState = useAtomValue(enumsLoadableAtom);

  const enums = enumsState.state === 'hasData' ? enumsState.data : null;
  const labels = useMemo(() => categoryLabels(enums), [enums]);

  const getLines = useCallback(
    () => form.getFieldValue(prefix),
    [form, prefix]
  );
  const setLines = useCallback(
    (lines: Line[]) => form.setFieldValue(prefix, lines),
    [form, prefix]
  );
  const updateLine = useCallback(
    (lineIndex: number, patch: Partial<Line>) =>
      setLines(
        getLines().map((line, i) =>
          i === lineIndex ? { ...line, ...patch } : line
        )
      ),
    [getLines, setLines]
  );

  const addLine = useCallback(() => {
    setLines([
      ...getLines(),
      {
        id: nextId(),
        rateCategories: [],
        reserveCategories: [],
        interestTypes: [],
        operator: '',
        rate: '',
        hurdleRate: null,
      },
    ]);
  }, [getLines, setLines]);

  const loadDefaults = useCallback(
    (interestType: InterestType) =>
      setLines(loadDefaultLines(getLines(), interestType)),
    [getLines, setLines]
  );

  const changeReserveCategories = useCallback(
    (lineIndex: number, value: ReserveCategory[]) =>
      setLines(setLineReserveCategories(getLines(), lineIndex, value)),
    [getLines, setLines]
  );

  const changeHurdle = useCallback(
    (lineIndex: number, value: number | null) =>
      setLines(setLineHurdle(getLines(), lineIndex, value)),
    [getLines, setLines]
  );

  const removeLine = useCallback(
    (lineIndex: number) =>
      setLines(getLines().filter((_, i) => i !== lineIndex)),
    [getLines, setLines]
  );

  return (
    <Accordion defaultExpanded>
      <AccordionSummary>
        <h4 className={styles.title}>Discount Rates</h4>
      </AccordionSummary>
      <AccordionDetails>
        <div
          className={styles.tableWrapper}
          onClick={(e) => e.stopPropagation()}
        >
          <p className={styles.description}>
            Each line applies one rate to every combination of its rate
            categories, reserve categories and interest types.
          </p>

          <form.Subscribe
            selector={(state) =>
              state.values.areas[areaIndex]?.discountRateLines ?? []
            }
          >
            {(lines) => (
              <DiscountRateLines
                areaIndex={areaIndex}
                lines={lines}
                enums={enums}
                labels={labels}
                onLineChange={updateLine}
                onReserveCategoriesChange={changeReserveCategories}
                onHurdleChange={changeHurdle}
                onRemove={removeLine}
                onAdd={addLine}
                onLoadDefaults={loadDefaults}
              />
            )}
          </form.Subscribe>
        </div>
      </AccordionDetails>
    </Accordion>
  );
}
